<template>
  <!-- eslint-disable -->
  <mi-select
    ref="chips"
    :model-value="combination.preCondition || []"
    outlined
    use-input
    :disable="disable"
    :error="combination.invalid"
    :error-message="combination.message"
    :placeholder="combination.preCondition?.length > 0 ? '' : 'eg.: AA001;BB001;BB002;'"
    multiple
    :hint="hasHint? 'Add a precondition to the analyses.' : ''"
    hide-dropdown-icon
    :label="customLabelShow ? customLabel : `Precondition ${ index +1 }`"
    class="col"
    :rules="choicesCombinationsInputRules"
    @new-value="(val, done) => createValue(val, combination, index, done)"
    @input-value="(val) => onChange(val, index)"
    @keydown="onKeyDown"
    @blur="onBlur"
    @focus="onFocus"
  >
    <template #selected-item="scope">
      <q-chip
        removable
        dense
        :tabindex="scope.tabindex"
        color="white"
        text-color="secondary"
        class="q-mr-md"
        :class="invalidChoices.has(scope.opt) ? 'q-chip-error' : 'q-chip-success'"
        @remove="removeValue(scope, combination, index)"
      >
        {{ scope.opt }}
      </q-chip>
    </template>
    <template v-if="showTooltip" #append>
      <product-model-settings-tooltip></product-model-settings-tooltip>
    </template>
    <template #default>
      <div v-if="showSuggestions" class="suggestions-box" @mousedown.stop @click.stop>
        <div
          v-for="option in options.suggestions"
          :key="option.id2"
          class="suggestions-wrapper"
          @click="selectSuggestion(option)"
        >
          <div class="suggestions">
            {{ option.id2 }} - {{ option.name }}
          </div>
        </div>
      </div>
    </template>
  </mi-select>
  <div
    v-if="shouldShowExpression"
    class="q-mt-lg logic-expression-container"
  >
      <h4 class="expression-label"> Precondition Expression</h4>
      <p class="logic-expression">
        {{ logicExpressions.expression }}
      </p>
  </div>
</template>

<script>
  import { computed, nextTick, ref, watch } from 'vue'
  import { containsSpecialChars } from '@/utils/validators/common'
  import {
    getAutocompleteSuggestions,
    getAutocompleteSuggestionsPasted,
    getLogicExpressions
  } from '@/api'
  import ProductModelSettingsTooltip from '../../product-model/settings/ProductModelSettingsTooltip.vue'

  export default {
    name: 'ChoicesCombinations',
    components: { ProductModelSettingsTooltip },
    props: {
      customLabel: {
        type: String,
        required: false,
        default: ''
      },
      customLabelShow: {
        type: Boolean,
        required: false,
        default: false
      },
      combination: {
        type: Object,
        required: true
      },
      index: {
        type: Number,
        required: false,
        default: 0
      },
      addChoices: {
        type: Function,
        required: false,
        default: () => {}
      },
      removeChoice: {
        type: Function,
        required: false,
        default: () => {}
      },
      updateInvalidValue: {
        type: Function,
        required: false,
        default: () => {}
      },
      invalidChoices: {
        type: Set,
        required: false,
        default: () => new Set()
      },
      showTooltip: {
        type: Boolean,
        default: true,
        required: false
      },
      hasHint: {
        type: Boolean,
        default: false,
        required: false
      },
      disable: {
        type: Boolean,
        required: false,
        default: false
      },
      choicesCombinationsInputRules: {
        type: Array,
        default: () => [],
        required: false
      },
      productModel: {
        type: Object,
        required: false,
        default: null
      },
      localStoragePreconditions: {
        type: Array,
        required: false,
        default: () => []
      },
      showExpressionOnFocus: {
        type: Boolean,
        required: false,
        default: false
      }
    },
    emits: ['focus', 'blur'],
    setup(props, { emit }) {
      const chips = ref(null)
      const showSuggestions = ref(false)
      const options = ref({})
      const logicExpressions = ref({})
      const selectedOptions = ref([])

      const inputValue = ref('')
      const isInputFocused = ref(false)

      const onFocus = () => {
        isInputFocused.value = true
        emit('focus')
      }
      const onBlur = () => {
        isInputFocused.value = false
        emit('blur')
      }

      const hasExpression = computed(() => !!logicExpressions.value?.expression)

      const shouldShowExpression = computed(() => (props.showExpressionOnFocus
        ? (isInputFocused.value && hasExpression.value)
        : hasExpression.value))

      const isComponentLimitReached = (preconditions, values) => preconditions.length
        + values.length > 20

      const validateSpecialChars = ({ selectedValue = '', settingIndex }) => {
        const hasSpecialChar = containsSpecialChars(selectedValue)

        if (hasSpecialChar) {
          props.updateInvalidValue(settingIndex, 'message', 'The separator is not allowed')
          return true
        }

        return false
      }

      const validateMaxChoicesLimitReached = ({ preconditions = [], values = [], settingIndex }) => {
        const isLimitReached = isComponentLimitReached(preconditions, values)

        if (isLimitReached) {
          props.updateInvalidValue(settingIndex, 'message', 'The maximum number of choices is 20')
          return true
        }
        return false
      }

      const removeValue = async (val, setting, settingIndex) => {
        const maxLimitReached = validateMaxChoicesLimitReached({ preconditions: setting.preCondition, settingIndex })
        props.updateInvalidValue(settingIndex, 'invalid', maxLimitReached)

        props.removeChoice(val, settingIndex)
        selectedOptions.value = selectedOptions.value.filter(opt => opt.id2 !== val.opt)
        if (selectedOptions.value.length > 0) {
          logicExpressions.value = await getLogicExpressions(selectedOptions.value)
        }
        else {
          logicExpressions.value = {}
        }
      }

      const createValue = (val, setting, settingIndex, done) => {
        const preconditions = setting?.preCondition || []
        const valueList = val.toUpperCase().split(';').filter(item => !!item)

        const invalidChars = validateSpecialChars({ selectedValue: val, settingIndex })
        const maxLimitReached = validateMaxChoicesLimitReached({ preconditions, values: valueList, settingIndex })

        props.updateInvalidValue(settingIndex, 'invalid', invalidChars || maxLimitReached)

        if (invalidChars || maxLimitReached) return

        valueList.map(v => v.trim())
          .filter(v => v.length > 0 && v.length < 6)
          .forEach(v => preconditions.push(v))

        const updatedConditions = [...new Set(preconditions)]

        nextTick(() => {
          done?.([...updatedConditions].pop())
        })

        props.addChoices(updatedConditions, settingIndex)
      }

      const onKeyDown = async event => {
        if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
          setTimeout(async () => {
            const preconditions = inputValue.value.split(';').map(v => v.trim()).filter(v => v)
            const payload = {
              productModelName: props.productModel.name,
              preconditions
            }
            const { suggestions } = await getAutocompleteSuggestionsPasted(payload)
            selectedOptions.value = [...selectedOptions.value, ...suggestions]
            logicExpressions.value = await getLogicExpressions(selectedOptions.value)
          }, 10)
        }
      }

      const onChange = async (value, settingIndex) => {
        inputValue.value = value
        if (!value) {
          props.updateInvalidValue(settingIndex, 'invalid', false)
          showSuggestions.value = false
        }
        else if (value.length >= 3 && value.length <= 5) {
          const payload = {
            productModelName: props.productModel.name,
            input: value
          }
          options.value = await getAutocompleteSuggestions(payload)
          showSuggestions.value = options.value?.suggestions?.length > 0
        }
        else {
          showSuggestions.value = false
        }
      }

      const selectSuggestion = async option => {
        const preconditions = props.combination?.preCondition || []
        selectedOptions.value.push(option)
        if (selectedOptions.value.length > 0) {
          logicExpressions.value = await getLogicExpressions(selectedOptions.value)
        }
        preconditions.push(option.id2)
        props.addChoices([...new Set(preconditions)], props.index)
        showSuggestions.value = false
        await nextTick()
        chips.value?.$refs?.qSelect?.updateInputValue('')
      }

      watch(
        () => [props.localStoragePreconditions, props.productModel],
        async ([preconditions, productModel]) => {
          if (!preconditions?.length || !productModel) return

          const { suggestions } = await getAutocompleteSuggestionsPasted({
            productModelName: productModel.name,
            preconditions
          })

          selectedOptions.value = suggestions || []

          if (selectedOptions.value.length > 0) {
            logicExpressions.value = await getLogicExpressions(selectedOptions.value)
          }
        },
        { immediate: true }
      )

      return {
        createValue,
        removeValue,
        onChange,
        showSuggestions,
        options,
        selectSuggestion,
        onKeyDown,
        chips,
        logicExpressions,
        selectedOptions,
        shouldShowExpression,
        onBlur,
        onFocus
      }
    }
  }
</script>

<style lang="scss" scoped>
  ::v-deep(.q-field) {
    // stylelint-disable declaration-no-important
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }

  ::v-deep(.q-field__append) {
    height: 0 !important;
    color: $mi-anthracite-800;
    cursor: pointer;
  }

  ::v-deep(.q-field__bottom) {
    padding: 2px !important;
  }

  ::v-deep(.q-field__bottom--stale) {
    transform: translateY(100%);
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
  }

  ::v-deep(.q-chip--dense .q-chip__icon) {
    font-size: 9px;
    padding: 2px;
    background-color: $mi-anthracite-800;
    border-radius: 50%;
    color: $mi-white;
    margin-left: 4px;
  }

  ::v-deep(.q-chip--dense .q-chip__icon--remove) {
    background-color: $mi-anthracite-800;
    opacity: 1;
  }

  ::v-deep(.q-chip) {
    margin-right: 0;
  }

  ::v-deep(.q-chip__content) {
    font-size: 12px;
    font-weight: 800;
    color: $mi-anthracite-800;
  }

  .q-chip-success {
    border: 2px solid $mi-anthracite-800 !important;
  }

  .q-chip-error {
    border: 2px solid $mi-red-700 !important;
    background-color: $mi-red-50 !important;

    ::v-deep(.q-chip__content) {
      color: $mi-red-700;
    }

    ::v-deep(.q-icon) {
      background-color: $mi-red-700 !important;
    }
  }

  .suggestions-box {
    width: 100%;
    position: absolute;
    background-color: #eaedf3;
    border: 1px solid #0d1117;
    z-index: 99999;
    top: 40px;
    left: 0;
    color: #0d1117;
  }

  .suggestions-wrapper {
    padding: 10px 12px;
    font-weight: 700;
    font-size: 12px;
    line-height: 18px;
    letter-spacing: .2px;
    vertical-align: middle;
    cursor: pointer;
  }

  .suggestions-wrapper:hover {
    background-color: #dfe2e8;
  }

  .expression-label {
    font-weight: 700;
    font-size: 12px;
    line-height: 14px;
    letter-spacing: .2px;
    margin: 5px 0;
  }

  .logic-expression {
    font-weight: 700;
    font-size: 14px;
    line-height: 14px;
    letter-spacing: .2px;
    padding: 10px 5px;
    background-color: #edf3f2;
  }
</style>
